<template>
  <div>
    <Card pt:content:class="p-0!" pt:body:class="p-2!">
      <template #content>
        <div class="flex flex-row w-full gap-2">
          <Card
            class="mt-auto border-surface-300 dark:border-surface-600 shadow-none"
            :class="editing ? 'h-[76vh]' : 'h-[58vh]'"
            pt:body:class="p-0!"
            pt:content:class="p-0!"
          >
            <template #content>
              <div class="flex flex-col h-full w-[400px] gap-5">
                <div class="flex flex-row justify-center gap-2 py-3 bg-surface-100 dark:bg-surface-800 rounded-t-md">
                  <p class="font-bold my-auto">Checklist Options</p>
                </div>
                <div class="flex flex-col justify-center gap-3">
                  <p class="font-bold text-center">Checklist Type</p>
                  <div class="flex flex-row gap-2 justify-evenly">
                    <RadioButtonGroup
                      v-model="selectedType"
                      name="type"
                      :options="types"
                      :on-change="checklistTypeChanged"
                      :disabled="!editing || checklists.length === 0"
                    ></RadioButtonGroup>
                  </div>
                </div>
                <Divider></Divider>
                <div class="flex flex-col justify-center gap-3">
                  <p class="font-bold text-center">Attach to Budget Item</p>
                  <Select
                    v-model="selectedItem"
                    :options="drawItems"
                    :disabled="!editing || selectedType !== 'Draw Item' || checklists.length === 0"
                    showClear
                    option-label="description"
                    class="w-[22rem] rounded mx-auto"
                    @change="checklists[activeTab].drawItemId = selectedItem == null ? null : selectedItem.drawItemId"
                  ></Select>
                </div>
                <Transition v-bind="fadeTransitionSlow">
                  <div v-show="editing">
                    <Divider></Divider>
                    <div class="flex flex-col justify-center gap-3">
                      <p class="font-bold text-center">Templates</p>
                      <Select
                        v-model="selectedTemplate"
                        :options="checklistTemplates"
                        :disabled="!editing"
                        showClear
                        option-label="name"
                        scrollHeight="20rem"
                        class="w-[22rem] rounded mx-auto"
                      ></Select>
                      <div class="flex flex-row w-[22rem] justify-center mx-auto gap-2">
                        <Button
                          v-tooltip.top="'Load'"
                          icon="pi pi-file-import"
                          outlined
                          class="p-2! w-full"
                          :disabled="selectedTemplate == null"
                          @click="tryLoadTemplate"
                        ></Button>
                        <Button
                          v-tooltip.top="'Save'"
                          icon="pi pi-save"
                          outlined
                          class="p-2! w-full"
                          :disabled="checklists.length === 0"
                          @click="trySaveTemplate"
                        ></Button>
                        <Button
                          v-tooltip.top="'Delete'"
                          icon="pi pi-trash"
                          outlined
                          class="p-2! w-full"
                          :disabled="selectedTemplate == null"
                          @click="tryDeleteTemplate"
                        ></Button>
                      </div>
                    </div>
                  </div>
                </Transition>
              </div>
            </template>
          </Card>

          <div class="w-full">
            <DynamicTabs
              class="mt-3"
              v-model:active-tab="activeTab"
              v-model:tabs="tabs"
              :show-add="editing"
              :show-delete="editing"
              :show-edit="editing"
              tab-name="Checklist"
              @tab-changed="updateTabInfo"
              @add-tab="addTab"
              @edit-tab="editTab"
              @delete-tab="deleteTab"
            ></DynamicTabs>
            <div :class="editing ? 'h-[76vh]' : 'h-[58vh]'">
              <ag-grid-vue
                theme="legacy"
                :rowData="checklistItems"
                :columnDefs="colDefs"
                :defaultColDef="defaultColDefs"
                :autoSizeStrategy="{ type: 'fitGridWidth' }"
                singleClickEdit
                stopEditingWhenCellsLoseFocus
                suppressDragLeaveHidesColumns
                class="h-full"
                :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
                :isFullWidthRow="(params: IsFullWidthRowParams) => params.rowNode.rowPinned === 'bottom'"
                :fullWidthCellRenderer="CustomAddNewCellRenderer"
                :fullWidthCellRendererParams="{
                  callback: () => addNewChecklistItem(),
                  label: 'checklist item',
                  display: canEdit,
                }"
                @grid-size-changed="resize"
                @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
              ></ag-grid-vue>
            </div>
          </div>
        </div>
        <div v-if="canEdit" v-show="!editing" class="flex justify-end pt-3">
          <Button icon="pi pi-pencil" label="Edit" @click="startEditingChecklists"></Button>
        </div>
        <SubmitOptions v-show="editing" class="pt-3" @cancelled="stopEditingChecklists" @submitted="submitChecklistChanges"></SubmitOptions>
      </template>
    </Card>
    <TSDialog :label="`${templateAction} Checklist`" v-model:visible="templateAction" icon="pencil" size="w-[600px]!">
      <template #content>
        <div class="flex flex-col gap-2 py-5">
          <label for="tabName" class="font-bold">Checklist Name:</label>
          <InputText name="tabName" type="text" class="h-10" v-model="templateName" autofocus v-on:keyup.enter="loadTemplateNameIsValid" />
          <SubmitButton :label="`${templateAction} Checklist`" @click="loadTemplateNameIsValid"></SubmitButton>
        </div>
      </template>
    </TSDialog>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import type { MenuItem } from "primevue/menuitem";
import type { Project } from "@/types/Projects";
import type { Checklist, ChecklistItem } from "@/types/Checklist";
import { usePropsStore } from "@/stores/props";
import CustomCheckboxRenderer from "@/components/cell-renderers/CustomCheckboxRenderer.vue";
import type { DrawItem } from "@/types/DrawItem";
import CustomAddNewCellRenderer from "../cell-renderers/CustomAddNewCellRenderer.vue";
import CustomDeleteIconCellRenderer from "@/components/cell-renderers/CustomDeleteIconCellRenderer.vue";
import { useConfirm } from "primevue/useconfirm";
import type { CellClassParams, GridApi, GridReadyEvent, IsFullWidthRowParams } from "ag-grid-community";
import { getTableData } from "@/services/Utility";
import { fadeTransitionSlow } from "@/services/Transitions";

const confirm = useConfirm();

const activeTab = ref(0);

const checklists = defineModel<Checklist[]>("checklists", { required: true });
const checklistTemplates = defineModel<Checklist[]>("checklistTemplates", { required: true });
const editing = defineModel<boolean>("editing", { required: true });
const editingTitle = defineModel<string>("editingTitle", { required: true });
const selectedItem = ref<DrawItem>();
const selectedTemplate = ref<Checklist | null>(checklistTemplates.value.length === 0 ? null : checklistTemplates.value[0]);

const checklistItems = computed<ChecklistItem[]>(() => (checklists.value.length !== 0 ? checklists.value[activeTab.value].items : []));

const templateAction = ref<"Rename" | "Save" | null>(null);
const templateName = ref("");
const addNewRowObject = ref([{}]);

enum ChecklistScope {
  PROJECT = 0,
  DRAW,
  DRAW_ITEM,
}

const tabs = computed<MenuItem[]>(() => {
  return checklists.value.map((checklist: Checklist) => {
    return {
      label: checklist.name,
      icon: "",
      visible: true,
      key: checklist.listId,
    };
  });
});

let checklistHistory = useManualRefHistory(checklists, { clone: true });
let checklistTemplateHistory = useManualRefHistory(checklistTemplates, { clone: true });
const checklistSubmitted = ref(false);

const types = ref(["Project", "Draw", "Draw Item"]);
const selectedType = ref<"Project" | "Draw" | "Draw Item">("Project");

interface Props {
  project: Project;
  canEdit: boolean;
  drawItems: DrawItem[];
}
const props = defineProps<Props>();

const updateTabInfo = (): void => {
  if (checklists.value.length !== 0) {
    const currentChecklist = checklists.value[activeTab.value];
    if (currentChecklist.drawItemId != null) {
      selectedType.value = "Draw Item";
      selectedItem.value = props.drawItems.find((item: DrawItem) => item.drawItemId === currentChecklist.drawItemId);
    } else if (currentChecklist.drawId != null) {
      selectedType.value = "Draw";
      selectedItem.value = undefined;
    } else {
      selectedType.value = "Project";
      selectedItem.value = undefined;
    }
  }
};

defineExpose({ updateTabInfo });

const toggleEditing = (value: boolean): void => {
  editing.value = value;
  editingTitle.value = value ? "Editing Checklists" : "";

  if (value) {
    addNewRowObject.value = [{}];
    selectedTemplate.value = checklistTemplates.value.length !== 0 ? checklistTemplates.value[0] : null;
  } else {
    addNewRowObject.value = [];
  }

  setTimeout(() => {
    gridApi.value?.setColumnsVisible(["delete"], editing.value);
    gridApi.value?.refreshCells({
      force: true,
      suppressFlash: true,
      columns: ["delete", "status", "title", "notes"], // colId of the column you want to refresh
    });
    gridApi.value?.setGridOption("pinnedBottomRowData", addNewRowObject.value);
    resize();
  }, 10);
};

const startEditingChecklists = (): void => {
  checklistHistory = useManualRefHistory(checklists, { clone: true });
  checklistTemplateHistory = useManualRefHistory(checklistTemplates, { clone: true });
  checklistHistory.commit();
  checklistTemplateHistory.commit();
  toggleEditing(true);
};

const submitChecklistChanges = (): void => {
  checklistHistory.commit();
  checklistTemplateHistory.commit();
  checklistSubmitted.value = true;

  axios.post(`/checklist/update/${props.project.projectDrawInfo.drawId}`, checklists.value).catch((e) => errorApi(e));
  axios.post("/checklist/templates/update", checklistTemplates.value).catch((e) => errorApi(e));

  stopEditingChecklists();
};

const stopEditingChecklists = (): void => {
  activeTab.value = 0;
  if (!checklistSubmitted.value) {
    checklistHistory.undo();
    checklistTemplateHistory.undo();
  }
  checklistSubmitted.value = false;
  updateTabInfo();
  toggleEditing(false);
};

const checklistTypeChanged = (): void => {
  const checklist = checklists.value[activeTab.value];
  if (selectedType.value === "Draw Item") {
    checklist.drawId = props.project.projectDrawInfo.drawId;
    checklist.scope = ChecklistScope.DRAW_ITEM;
  } else if (selectedType.value === "Draw") {
    checklist.drawId = props.project.projectDrawInfo.drawId;
    checklist.drawItemId = null;
    checklist.scope = ChecklistScope.DRAW;
    selectedItem.value = undefined;
  } else {
    checklist.drawId = null;
    checklist.drawItemId = null;
    checklist.scope = ChecklistScope.PROJECT;
    selectedItem.value = undefined;
  }
};

const addNewChecklistItem = (): void => {
  if (checklists.value.length == 0) {
    error("Invalid Action", "You need to add a checklist first");
    return;
  }

  const newChecklistItem: ChecklistItem = {
    itemId: "",
    listId: checklists.value[activeTab.value].listId,
    status: false,
    title: "",
    notes: "",
    ordinal: checklistItems.value.length,
  };

  gridApi.value?.applyTransaction({
    add: [newChecklistItem],
  });

  checklists.value[activeTab.value].items = getTableData(gridApi.value);
};

const deleteChecklistItem = (item: ChecklistItem): void => {
  for (let i = item.ordinal; i < checklists.value[activeTab.value].items.length; i++) {
    checklists.value[activeTab.value].items[i].ordinal -= 1;
  }

  gridApi.value?.applyTransaction({
    remove: [item],
  });
  checklists.value[activeTab.value].items = getTableData(gridApi.value);
};

const addTab = (newTabName: string): void => {
  const newChecklist: Checklist = {
    listId: "",
    projectId: props.project.projectInfo.projectId,
    drawId: null,
    drawItemId: null,
    name: newTabName,
    scope: ChecklistScope.PROJECT,
    ordinal: checklists.value.length,
    items: [],
  };

  checklists.value.push(newChecklist);
  updateTabInfo();
};

const editTab = (newTabName: string, editTabIndex: number): void => {
  checklists.value[editTabIndex].name = newTabName;

  updateTabInfo();
};

const deleteTab = (index: number): void => {
  activeTab.value = 0;
  checklists.value.splice(index, 1);
  for (let i = index; i < checklists.value.length; i++) {
    checklists.value[i].ordinal -= 1;
  }
  updateTabInfo();
};

const tryLoadTemplate = (): void => {
  if (selectedTemplate.value == null) return;

  let templateName = selectedTemplate.value.name;

  const overrideChecklist = checklists.value.find((checklist: Checklist) => checklist.name.toUpperCase() === templateName.toUpperCase());

  if (overrideChecklist != null) {
    confirmPopup(
      confirm,
      "Load Checklist Template",
      `Are you sure you want to override the current checklist for ${selectedTemplate.value.name}?`,
      () => loadTemplate(templateName, overrideChecklist),
      askTemplateRename
    );
  } else {
    loadTemplate(templateName, null);
  }
};

const askTemplateRename = (): void => {
  templateName.value = "";
  setTimeout(() => {
    confirmPopup(
      confirm,
      "Rename Tab",
      "Would you like to load this template with a new name?",
      () => (templateAction.value = "Rename"),
      () => info("Load Cancelled", "")
    );
  }, 50);
};

const loadTemplateNameIsValid = (): void => {
  if (templateName.value == null || templateName.value.trim() === "") {
    error("Invalid Name", "The tab name may not be empty");
    return;
  }

  if (templateAction.value === "Rename") {
    const existingTab = tabs.value.find((tab: MenuItem) => tab.label?.toString().toUpperCase() === templateName.value.toUpperCase());
    if (existingTab != null) {
      error("Invalid Name", "There is already a tab with this name");
      return;
    }
    loadTemplate(templateName.value, null);
  } else {
    const existingTemplate = checklistTemplates.value.find((template: Checklist) => template.name.toUpperCase() === templateName.value.toUpperCase());
    if (existingTemplate != null) {
      confirmPopup(
        confirm,
        "Overwrite Existing Template",
        `A Template with the name ${templateName.value} already exists, do you want to overwrite it?`,
        () => saveTemplate(templateName.value, existingTemplate),
        () => info("Save Cancelled", "")
      );
    } else {
      saveTemplate(templateName.value, null);
    }
  }
};

const loadTemplate = (newTemplateName: string, overrideChecklist: Checklist | null): void => {
  if (selectedTemplate.value == null) return;

  if (overrideChecklist != null) {
    if (selectedTemplate.value.scope === ChecklistScope.PROJECT) {
      overrideChecklist.drawId = null;
      overrideChecklist.drawItemId = null;
      overrideChecklist.scope = ChecklistScope.PROJECT;
    } else if (selectedTemplate.value.scope === ChecklistScope.DRAW) {
      overrideChecklist.drawId = props.project.projectDrawInfo.drawId;
      overrideChecklist.drawItemId = null;
      overrideChecklist.scope = ChecklistScope.DRAW;
    } else {
      overrideChecklist.drawId = props.project.projectDrawInfo.drawId;
      overrideChecklist.scope = ChecklistScope.DRAW_ITEM;
    }

    overrideChecklist.scope = selectedTemplate.value.scope;
    setItems(overrideChecklist, selectedTemplate.value.items);
    const index = checklists.value.indexOf(overrideChecklist);
    if (index !== -1) activeTab.value = index;
  } else {
    const newChecklist: Checklist = {
      listId: "",
      projectId: props.project.projectInfo.projectId,
      drawId: selectedTemplate.value.scope !== ChecklistScope.PROJECT ? props.project.projectDrawInfo.drawId : null,
      drawItemId: null,
      name: newTemplateName,
      scope: selectedTemplate.value.scope,
      ordinal: checklists.value.length,
      items: [],
    };
    setItems(newChecklist, selectedTemplate.value.items);
    checklists.value.push(newChecklist);
    activeTab.value = checklists.value.length - 1;
  }

  templateAction.value = null;
  templateName.value = "";
  updateTabInfo();
};

const trySaveTemplate = (): void => {
  templateName.value = selectedTemplate.value != null ? selectedTemplate.value.name : "";
  templateAction.value = "Save";
};

const saveTemplate = (newTemplateName: string, overrideTemplate: Checklist | null): void => {
  const currentChecklist = checklists.value[activeTab.value];
  if (overrideTemplate != null) {
    overrideTemplate.scope = currentChecklist.scope;
    overrideTemplate.name = newTemplateName;
    setItems(overrideTemplate, currentChecklist.items);
  } else {
    const newTemplate: Checklist = {
      listId: "",
      projectId: null,
      drawId: null,
      drawItemId: null,
      name: newTemplateName,
      scope: currentChecklist.scope,
      ordinal: -1,
      items: [],
    };
    setItems(newTemplate, currentChecklist.items);
    checklistTemplates.value.push(newTemplate);
  }

  templateAction.value = null;
  templateName.value = "";
  updateTabInfo();
};

const setItems = (checklist: Checklist, existingItems: ChecklistItem[]): void => {
  const items: ChecklistItem[] = [];
  existingItems.forEach((templateItem: ChecklistItem) => {
    const item: ChecklistItem = {
      itemId: "",
      listId: checklist.listId,
      status: false,
      title: templateItem.title,
      notes: templateItem.notes,
      ordinal: items.length,
    };
    items.push(item);
  });
  checklist.items = items;
};

const tryDeleteTemplate = (): void => {
  if (selectedTemplate.value == null) return;

  confirmPopup(
    confirm,
    "Delete Checklist Template",
    `Are you sure you want to delete checklist template ${selectedTemplate.value.name}?`,
    deleteTemplate,
    () => info("Delete Cancelled", "")
  );
};

const deleteTemplate = (): void => {
  if (selectedTemplate.value == null) return;
  const index = checklistTemplates.value.indexOf(selectedTemplate.value);
  if (index === -1) return;

  checklistTemplates.value.splice(index, 1);

  selectedTemplate.value = checklistTemplates.value.length !== 0 ? checklistTemplates.value[0] : null;
};

const gridApi = ref<GridApi>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const changeStatus = (rowIndex: number, status: boolean): void => {
  const rowData = gridApi.value?.getDisplayedRowAtIndex(rowIndex);
  if (rowData == null) return;
  rowData.setDataValue("status", status);
  checklists.value[activeTab.value].items = getTableData(gridApi.value);
  if (!editing.value) {
    axios.post(`/checklist/update-status/${rowData.data.itemId}/${status}`).catch((e) => errorApi(e));
  }
};

const resize = (): void => {
  gridApi.value?.sizeColumnsToFit();
};

const cellClass = (data: ChecklistItem): string => {
  if (data.status) return "line-through bg-secondary-500/20";
  return "";
};

const defaultColDefs = {
  floatingFilter: false,
  enableCellChangeFlash: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  cellDataType: "text",
  sortable: false,
};

const colDefs = ref([
  {
    field: "delete",
    headerName: "",
    cellClass: "text-center flex items-center justify-center",
    cellRenderer: CustomDeleteIconCellRenderer,
    cellRendererParams: {
      deleteConfirmMessage: (data: ChecklistItem) => `Are you sure you want to delete item "${data.title}" from the checklist?`,
      deleteConfirmHeader: () => "Delete Checklist Item",
      callback: deleteChecklistItem,
      checkDeletable: (data: ChecklistItem) => true,
    },
    width: 50,
    minWidth: 50,
    hide: !editing.value,
  },
  {
    field: "status",
    headerName: "Status",
    headerClass: "grid-align-center text-center",
    minWidth: 100,
    width: 100,
    cellDataType: "boolean",
    cellClass: "text-center",
    cellRenderer: CustomCheckboxRenderer,
    cellRendererParams: () => {
      return {
        setValue: changeStatus,
        disabled: !editing.value,
      };
    },
  },
  {
    field: "title",
    headerName: "Title",
    minWidth: 150,
    width: 400,
    editable: () => editing.value,
    cellClass: (params: CellClassParams) => cellClass(params.data),
  },
  {
    field: "notes",
    headerName: "Notes",
    minWidth: 150,
    width: 800,
    editable: () => editing.value,
    cellClass: (params: CellClassParams) => cellClass(params.data),
  },
]);

onBeforeMount(async (): Promise<void> => {
  updateTabInfo();
});
</script>
